import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Code } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  public render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Error Card */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-b border-red-500/20 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white mb-1">
                      Oops! Something went wrong
                    </h1>
                    <p className="text-slate-400">
                      Don't worry, this is just a temporary hiccup
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Error Message */}
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <p className="text-slate-300 text-sm font-mono">
                    {error?.message || 'An unexpected error occurred'}
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={this.handleRefresh}
                    className="flex-1 flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-medium px-6 py-3 rounded-lg transition-all duration-200 shadow-lg shadow-cyan-500/20"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Refresh Page
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-medium px-6 py-3 rounded-lg transition-all duration-200"
                  >
                    <Home className="w-5 h-5" />
                    Go Home
                  </button>
                </div>

                {/* Toggle Details */}
                <button
                  onClick={this.toggleDetails}
                  className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-slate-300 text-sm font-medium py-2 transition-colors"
                >
                  <Code className="w-4 h-4" />
                  {showDetails ? 'Hide' : 'Show'} Technical Details
                </button>

                {/* Error Details (Collapsible) */}
                {showDetails && errorInfo && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                    {/* Stack Trace */}
                    <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                      <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                        <Code className="w-4 h-4" />
                        Stack Trace
                      </h3>
                      <div className="bg-black/30 rounded p-3 max-h-64 overflow-auto">
                        <pre className="text-xs text-red-400 font-mono whitespace-pre-wrap">
                          {error?.stack}
                        </pre>
                      </div>
                    </div>

                    {/* Component Stack */}
                    <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                      <h3 className="text-sm font-semibold text-slate-300 mb-3">
                        Component Stack
                      </h3>
                      <div className="bg-black/30 rounded p-3 max-h-64 overflow-auto">
                        <pre className="text-xs text-orange-400 font-mono whitespace-pre-wrap">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Help Text */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <p className="text-blue-300 text-sm">
                    <strong>💡 Pro Tip:</strong> If this error persists after refreshing, 
                    try clearing your browser cache or contact support with the technical 
                    details above.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-900/30 border-t border-slate-700 px-6 py-4">
                <p className="text-slate-500 text-xs text-center">
                  Error occurred at {new Date().toLocaleString()} • 
                  MetaTalio Staffing Platform v1.0
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
